import Axios, { CancelTokenSource } from 'axios';
import moment from 'moment';
import 'moment-timezone';
import {
    AnalyticsEngine,
    AnalyticsFilter,
    AnalyticsGroupBy,
    AnalyticsProjection,
    AnalyticsRequest,
} from '@sprinklr/stories/analytics/AnalyticsRequest';
import { AnalyticsResult } from '@sprinklr/stories/analytics/AnalyticsResult';
import DataSet, {
    DataSetRow,
    ReadonlyDataSetRow,
    ViralRowData,
} from '@sprinklr/stories/analytics/DataSet';
import Dimension from '@sprinklr/stories/analytics/Dimension';
import {
    FieldType,
    FieldDataType,
    ReportingRequest,
    ReportingResponse,
} from '@sprinklr/stories/reporting/types';
import Metric from '@sprinklr/stories/analytics/Metric';
import BulkItem from '@sprinklr/stories/analytics/BulkItem';
import SLAFrequency from '@sprinklr/stories/analytics/SLAFrequency';
import LookupItem from '@sprinklr/stories/analytics/LookupItem';
import PostItem, { BulkLookup } from '@sprinklr/stories/analytics/PostItem';
import { toSpaceBatchRequest } from '@sprinklr/stories-convert/request/toSpaceRequest';
import toReportingRequests from '@sprinklr/stories-convert/request/toReportingRequests';
import { PostsRequest } from '@sprinklr/stories/post/PostsRequest';
import { SpaceBatchRequest, SpaceRequest } from '@sprinklr/stories/reporting/types';
import {
    ensureProjectionOrder,
    getDataType,
    getHeading,
    getProjectionOrigOrderFromRequest,
    getType,
} from './helpers';
import { toAnalyticsTimePeriod } from '@sprinklr/stories-convert/request/toAnalyticsRequest';

// TODO: these @sprinklr/display-builder imports need to go away
import TimePeriod from '@sprinklr/display-builder/models/TimePeriod/TimePeriod';
import { WidgetDimensionKey } from '@sprinklr/display-builder/utils/Widget/WidgetDimensionKey';
import SprinklrAPIService, {
    BulkLookupValues,
    PromiseWithCancel,
} from '@sprinklr/display-builder/services/SprinklrAPIService/SprinklrAPIService';
import MetaInfoService from '@sprinklr/display-builder/services/MetaInfoService/MetaInfoService';
import TrendService from '@sprinklr/display-builder/services/TrendService/TrendService';
import InsightsItem from '@sprinklr/stories/analytics/InsightsItem';
import TimePeriodService from 'services/TimePeriodService/TimePeriodService';
import {
    buildAnalyticsProjectionHeader,
    getTopicClusterHeading,
    pivotTopicCluster,
    removeUnusedDecorators,
    TOPIC_CLUSTER_HEADINGS,
} from '@sprinklr/stories/analytics/helpers';
import { groupByOrProjectionAlternateHeading } from 'utils/Widget/WidgetUtils';
import {
    ViralContributor,
    ViralInsight,
    ViralTrendsRequest,
} from '@sprinklr/stories/analytics/ViralTrends';
import toMomentUnitOfTime from '@sprinklr/stories-convert/request/toMomentUnitOfTime';
import PostsService from '@sprinklr/stories-services/PostsService/PostsService';
import PostsProviderReporting from '@sprinklr/stories-services/PostsService/providers/PostsProviderReporting';
import PostsTransform from '@sprinklr/stories-services/PostsService/PostsTransform';
import PostsResolve from '@sprinklr/stories-services/PostsService/PostsResolve';
import SocialNetworkImageService from '@sprinklr/stories-services/PostsService/SocialNetworkImageService';
import ObjectUtils from 'utils/ObjectUtils/ObjectUtils';
import Direction from '@sprinklr/stories/analytics/Direction';
import _set from 'lodash/set';
import { MULTI_TIME_PERIOD_KEY } from './constants';
import _isUndefined from 'lodash/isUndefined';
import i18n from '@sprinklr/display-builder/i18n';
import { ApplicationIdentifier } from '@sprinklr/display-builder/config';

export type DimensionLookup = {
    index: number;
    groupBy: AnalyticsGroupBy;
    values: string[];
};

export default class AnalyticsService {
    // TODO: figure out which dimensions can't be filtered
    static cantLookup = [
        'SEM_CUSTOM_PHRASES_SEM_PHRASES_SENTIMENT',
        'WORD_CLOUD_MESSAGE',
        'WORD_CLOUD_TITLE',
        'SEM_CATEGORIES',
        'ACTIONS',
        'THINGS',
        'TOP_TERMS',
        'EMOTIONS',
        'PHRASE_COLLOCATIONS',
        'CITY_INSTA_TEST',
        'TITLE_TOPIC_CLUSTER',
        'MESSAGE_TOPIC_CLUSTER',
        'TOPIC_CLUSTER',
        'HASHTAGS',
    ];

    // are these separate concerns?
    static cantFilter = [...AnalyticsService.cantLookup, 'DAY_OF_TIME_RANGE'];

    static canFilter(engine: AnalyticsEngine, groupBy: AnalyticsGroupBy) {
        // Special-case for STORY_MESSAGE.  This function needs to return
        // false but canLookup needs to return true
        if (engine === 'STORY_MESSAGE') {
            switch (groupBy.dimensionName) {
                case 'ES_MESSAGE_ID':
                case 'WEB_TITLE_ES_MESSAGE_ID':
                    return false;
            }
        }

        return (
            groupBy.groupType === 'FIELD' &&
            AnalyticsService.cantFilter.indexOf(groupBy.dimensionName) === -1
        );
    }

    static canLookup(groupBy: AnalyticsGroupBy) {
        return (
            (groupBy.groupType === 'FIELD' || groupBy.groupType === 'RANGE') &&
            AnalyticsService.cantLookup.indexOf(groupBy.dimensionName) === -1
        );
    }

    static buildViralTrendsRequest(
        reportingRequest: ReportingRequest,
        viralRow: ReadonlyDataSetRow | any[],
        contributors?: ViralContributor[]
    ): ViralTrendsRequest {
        let filters = reportingRequest.filters;
        const viralFilters: AnalyticsFilter[] = [];
        let startTime = reportingRequest.startTime;
        let endTime = reportingRequest.endTime;

        reportingRequest.groupBys.forEach((groupBy, dimensionIndex) => {
            const isFilterable =
                groupBy.groupType === 'FIELD' ||
                groupBy.groupType === 'DAY_OF_WEEK' ||
                groupBy.groupType === 'MONTH_OF_YEAR';

            let dimensionValue = viralRow[dimensionIndex];
            if (dimensionValue instanceof Date) {
                dimensionValue = dimensionValue.getTime();
            } else if (typeof dimensionValue === 'object' && dimensionValue.id) {
                dimensionValue = dimensionValue.id;
            }

            if ([null, undefined, ''].includes(dimensionValue)) {
                return;
            }

            if (!isFilterable) {
                if (groupBy.groupType === 'DATE_HISTOGRAM' && groupBy.details?.interval) {
                    startTime = dimensionValue;

                    const [amount, unit] = toMomentUnitOfTime(groupBy.details.interval);
                    endTime = moment(dimensionValue)
                        .tz(reportingRequest.timeZone)
                        .add(amount, unit)
                        .valueOf();
                }
                return;
            }

            filters = filters.filter(filter => filter.dimensionName !== groupBy.dimensionName);

            viralFilters.push({
                dimensionName: groupBy.dimensionName,
                filterType: 'IN',
                values: [dimensionValue],
            });
        });

        const stripped = {
            ...reportingRequest,
            filters: [...filters, ...viralFilters],
            sorts: undefined,
            groupBys: undefined,
            page: undefined,
            pageSize: undefined,
            skipResolve: undefined,
            additional: {
                ...reportingRequest.additional,
                TABULAR: 'false',
                showRolloverTrends: undefined,
                viralTrendContributors: contributors ? contributors.join(',') : undefined,
            },
            startTime,
            endTime,
        };
        return stripped as ViralTrendsRequest;
    }

    sprinklrAPIService: SprinklrAPIService;
    metaInfoService: MetaInfoService;
    trendService: TrendService;

    constructor(
        sprinklrAPIService: SprinklrAPIService,
        metaInfoService: MetaInfoService,
        trendService: TrendService,
        private applicationIdentifier: ApplicationIdentifier
    ) {
        this.sprinklrAPIService = sprinklrAPIService;
        this.metaInfoService = metaInfoService;
        this.trendService = trendService;
        this.applicationIdentifier = applicationIdentifier;
    }

    getDimensionLookups(dataSets: DataSet[], request: AnalyticsRequest): DimensionLookup[] {
        const dimensionLookups: DimensionLookup[] = [];

        request.groupBys.forEach((groupBy: AnalyticsGroupBy, index) => {
            if (!AnalyticsService.canLookup(groupBy)) {
                return;
            }

            dimensionLookups.push({
                index,
                groupBy,
                // TODO: use Set?
                values: [],
            });
        });

        if (dimensionLookups.length === 0) {
            return dimensionLookups;
        }

        // gather all the possible values that need to be looked up
        dataSets.forEach((dataSet: DataSet) => {
            if (dataSet.dimensions.length === 0) {
                return;
            }

            dimensionLookups.forEach((dimensionLookup: DimensionLookup) => {
                dataSet
                    .categories(dataSet.dimensions[dimensionLookup.index])
                    .forEach((value: string) => {
                        // TODO: use Set?
                        if (!dimensionLookup.values.includes(value)) {
                            dimensionLookup.values.push(value);
                        }
                    });
            });
        });

        return dimensionLookups;
    }

    applyDimensionLookups(
        dataSets: DataSet[],
        dimensionLookups: DimensionLookup[],
        request: AnalyticsRequest,
        cancelSource?: CancelTokenSource
    ): Promise<AnalyticsResult> {
        // make all the lookup requests
        const promises = dimensionLookups
            .filter((dimensionLookup: DimensionLookup) => {
                return dimensionLookup.values.length > 0;
            })
            .map((dimensionLookup: DimensionLookup) => {
                return this.sprinklrAPIService.bulkLookup(
                    request.reportingEngine,
                    request.report,
                    WidgetDimensionKey.create(dimensionLookup.groupBy),
                    dimensionLookup.values,
                    request.timePeriods && request.timePeriods[0]
                        ? TimePeriodService.createFromAnalyticsTimePeriod(request.timePeriods[0])
                        : null,
                    cancelSource
                );
            });

        const experienceScoreMetrics = [
            'Experience_Score',
            'Experience_Score___Message',
            'Product_Insights_Experience_Score',
        ];

        const postLookup: BulkLookup = {};
        let postValues;

        // after all lookup requests resolve, create derived datasets with the replacement name values
        return Axios.all(promises)
            .then(async (results: BulkLookupValues[]) => {
                // For posts, do additional bulklookup for any needed dimensions
                results.forEach((lookupValues: BulkLookupValues, index) => {
                    const dimensionLookup = dimensionLookups[index];
                    switch (dimensionLookup?.groupBy?.dimensionName) {
                        case 'POST_ID':
                        case 'REPLY_POST_ID':
                            for (const id in lookupValues) {
                                PostItem.addToLookup(lookupValues[id], postLookup);
                            }
                            break;
                    }
                });

                postValues = await this.applyPostLookups(request, postLookup, cancelSource);

                return results;
            })
            .then(
                (results: BulkLookupValues[]): AnalyticsResult => {
                    return {
                        request,
                        dataSets: dataSets.map(
                            (dataSet: DataSet): DataSet => {
                                return dataSet.derivedDataset((rows): any[][] => {
                                    return rows.map((row): any[] => {
                                        const hasScore = !!dataSet.metrics.find(
                                            metric =>
                                                experienceScoreMetrics.indexOf(metric.name) !== -1
                                        );
                                        const isInsight = !!request.filters?.find(
                                            filter => filter.dimensionName === 'HIERARCHY_ID'
                                        );
                                        const newRow: DataSetRow = row.slice();
                                        newRow.isViral = row.isViral;
                                        results.forEach((lookupValues: BulkLookupValues, index) => {
                                            const dimensionLookup = dimensionLookups[index];
                                            const id = newRow[dimensionLookup.index];
                                            let name = lookupValues[id] || id;

                                            // Bulklookup now sends a "__deleted" mapping with an
                                            // array of input ids that were either ignored or
                                            // are records that were deleted from database.  In either
                                            // case we filter out these rows from result
                                            const isDeleted =
                                                lookupValues?.__deleted?.length &&
                                                lookupValues.__deleted.indexOf(id) !== -1;

                                            // TODO: this is gross because it's hard to read and unit test
                                            if (typeof name === 'object') {
                                                if (
                                                    name instanceof SLAFrequency &&
                                                    request.additional.slaConfig
                                                ) {
                                                    // Populate SLA objective
                                                    name.objective = JSON.parse(
                                                        request.additional.slaConfig
                                                    ).slaObjective;
                                                } else if (name instanceof BulkItem) {
                                                    // No action -- use as is.
                                                } else if (hasScore && isInsight) {
                                                    const color = null;
                                                    name = new InsightsItem(
                                                        id,
                                                        name.message ?? name.label ?? name.name,
                                                        color
                                                    );
                                                } else {
                                                    /**
                                                     *  @see BulkItem.normalize
                                                     */
                                                    if (
                                                        PostItem.canCreate(dimensionLookup?.groupBy)
                                                    ) {
                                                        name = PostItem.create(
                                                            name?.id ?? id,
                                                            name,
                                                            postValues
                                                        );
                                                    } else if (
                                                        name.contactInfo &&
                                                        name.contactInfo.fullName
                                                    ) {
                                                        name = name.contactInfo.fullName;
                                                    } else if (name.fullName) {
                                                        name = name.fullName;
                                                    } else if (name.title) {
                                                        name = name.title;
                                                    } else if (name.label) {
                                                        name = name.label;
                                                    } else if ('name' in name) {
                                                        const icon = name.thumbnail;
                                                        const image = name.socialAsset?.mediaUrl;

                                                        name = new LookupItem(
                                                            // paid can have multiple name.id so we use id
                                                            request.reportingEngine === 'PAID'
                                                                ? id
                                                                : name?.id ?? id,
                                                            // use || to avoid empty strings
                                                            name.displayName || name.name
                                                        );

                                                        if (
                                                            LookupItem.hasIcon(
                                                                dimensionLookup?.groupBy
                                                            )
                                                        ) {
                                                            name.setIconUrl(icon);
                                                        }

                                                        if (
                                                            LookupItem.hasImage(
                                                                dimensionLookup?.groupBy
                                                            )
                                                        ) {
                                                            name.setImageUrl(image);
                                                        }
                                                    } else if (id === '1') {
                                                        name = new LookupItem(id, name);
                                                    } else {
                                                        name = id;
                                                    }
                                                }
                                            } else if (typeof name === 'string') {
                                                if (
                                                    isDeleted &&
                                                    PostItem.canCreate(dimensionLookup?.groupBy)
                                                ) {
                                                    name = PostItem.createUnknown(id);
                                                }
                                            }

                                            newRow[dimensionLookup.index] = name;
                                        });
                                        return newRow;
                                    });
                                });
                            }
                        ),
                    };
                }
            );
    }

    private async applyPostLookups(
        request: AnalyticsRequest,
        lookup: BulkLookup,
        cancelSource?: CancelTokenSource
    ) {
        const keys = Object.keys(lookup);

        if (keys.length) {
            return await this.sprinklrAPIService.bulkLookupMulti(
                request.reportingEngine as any,
                request.report,
                keys.map(key => new WidgetDimensionKey(key as any)),
                keys.map(key => lookup[key]),
                null,
                cancelSource
            );
        }

        return {};
    }

    async applyGroupLimits(
        request: AnalyticsRequest,
        cancelSource?: CancelTokenSource
    ): Promise<AnalyticsRequest> {
        let groupBys = request.groupBys;
        const timePeriods = request.timePeriods;
        const projections = request.projections;

        const scrubLimits = (analyticsRequest: AnalyticsRequest): AnalyticsRequest => {
            const requestClone = ObjectUtils.copy(analyticsRequest);
            return {
                ...requestClone,
                groupBys: (requestClone.groupBys ?? []).map(groupBy =>
                    AnalyticsService.canFilter(requestClone.reportingEngine, groupBy)
                        ? {
                              ...groupBy,
                              limit: undefined,
                              limitType: 'NONE',
                          }
                        : groupBy
                ),
            };
        };

        // limits only make sense and appear in the UI when there's more than 1 groupBy
        if (!groupBys || groupBys.length === 0 || !projections || projections.length === 0) {
            return scrubLimits(request);
        }

        const firstMetric = projections[0];

        // No need to do a preflight request if there is only one groupBy
        if (groupBys.length === 1) {
            const firstGroupBy = groupBys[0];
            if (firstGroupBy.limitType && firstGroupBy.limitType !== 'NONE' && firstGroupBy.limit) {
                const newRequest = scrubLimits(request);

                if (_isUndefined(newRequest.limit)) {
                    newRequest.limit = firstGroupBy.limit;
                }

                if (_isUndefined(newRequest.sorts)) {
                    newRequest.sorts = [
                        {
                            heading: firstMetric.heading,
                            order: firstGroupBy.limitType === 'TOP' ? 'DESC' : 'ASC',
                        },
                    ];
                }

                return newRequest;
            }
        }

        groupBys = groupBys.filter(groupBy => {
            return (
                // don't apply groupBy limits if the dimension values won't work as a filter but Space API can try
                /** @see toSpaceGroupBy */
                AnalyticsService.canFilter(request.reportingEngine, groupBy) &&
                groupBy.limitType &&
                groupBy.limitType !== 'NONE' &&
                groupBy.groupType !== 'DATE_HISTOGRAM' &&
                groupBy.limit
            );
        });

        if (groupBys.length === 0 || !timePeriods || timePeriods.length === 0) {
            return scrubLimits(request);
        }

        const timePeriod = timePeriods[0];

        const limitFilters: AnalyticsFilter[] = [];
        for (const groupBy of groupBys) {
            const groupByClone: AnalyticsGroupBy = Object.assign({}, groupBy);
            const requestClone: AnalyticsRequest = Object.assign({}, request);

            requestClone.projections = [firstMetric];
            requestClone.groupBys = [groupByClone];
            requestClone.timePeriods = [timePeriod];
            requestClone.filters = requestClone.filters.concat(limitFilters);
            groupByClone.limitType = 'NONE';

            const sorts = (requestClone.sorts ?? [])
                .slice()
                .filter(sort => sort.heading !== firstMetric.heading);
            sorts.unshift({
                heading: firstMetric.heading,
                order: groupBy.limitType === 'TOP' ? 'DESC' : 'ASC',
            });

            requestClone.sorts = sorts;
            requestClone.limit = groupBy.limit;

            const result = await this.getDataFromSpace(requestClone, cancelSource, true);

            // get the dimension value from each row, and flatten
            let values = result.dataSets[0].rows.map(row => row[0].toString());

            // sometimes the limit doesn't seem to work, so we will force it
            if (values.length > groupBy.limit) {
                values = values.slice(groupBy.limit, values.length);
            }

            // Created Time values aren't working when using "IN".
            // so use "BETWEEN" instead
            if (
                groupByClone.dimensionName === 'SN_CREATED_TIME' ||
                groupByClone.dimensionName === 'snCTm'
            ) {
                // Sort ASC by date
                const sorted = values.sort((a, b) => {
                    return new Date(a).getTime() - new Date(b).getTime();
                });

                const beginTime = sorted.length ? moment(sorted[0]) : null;
                let endTime = sorted.length ? moment(sorted[sorted.length - 1]) : null;

                // Make sure that endTime is at the end of its interval
                endTime = TimePeriod.getEndOf(endTime, groupByClone.details?.interval);

                limitFilters.push({
                    dimensionName: groupByClone.dimensionName,
                    filterType: 'BETWEEN',
                    values: beginTime ? [beginTime.valueOf(), endTime.valueOf()] : [],
                    details: groupByClone.details,
                });
            } else {
                limitFilters.push({
                    dimensionName: groupByClone.dimensionName,
                    filterType: 'IN',
                    values,
                    details: groupByClone.details,
                });
            }
        }

        const modifiedRequest: AnalyticsRequest = scrubLimits(request);
        modifiedRequest.filters.push(...limitFilters);
        return modifiedRequest;
    }

    static applyProjectionFilters(request: AnalyticsRequest | PostsRequest) {
        if (!request.filters) {
            return;
        }

        const isProjectionFilter = filter => {
            return (
                filter?.details?.type === 'MEASUREMENT' ||
                filter?.details?.type === 'CUSTOM_MEASUREMENT' ||
                filter?.details?.type === 'CALCULATED'
            );
        };

        request.projectionFilters = request.filters.filter(filter => isProjectionFilter(filter));
        request.filters = request.filters.filter(filter => !isProjectionFilter(filter));
    }

    async applyFilters(request: AnalyticsRequest, cancelSource?: CancelTokenSource): Promise<any> {
        if (request.filters?.length) {
            request.filters = request.filters.filter(
                filter => !('ACCOUNT_ID' === filter.dimensionName && filter.details?.allValues)
            ); // Remove legacy all accounts filter behavior
        }
        const filterPromises = request.filters?.length
            ? request.filters.map(filter => {
                  if (filter.filterType === 'RANK') {
                      return this.rankedFilter(filter, request, cancelSource);
                  } else {
                      return new Promise(resolve => {
                          resolve(filter);
                      });
                  }
              })
            : [];

        return filterPromises && Promise.all(filterPromises);
    }

    applySlaConfig(request: AnalyticsRequest, slaConfig: any) {
        if (!request.additional) {
            request.additional = {};
        }

        if (request?.additional?.slaConfig && slaConfig) {
            request.additional.slaConfig = slaConfig;

            if (!request.projections) {
                request.projections = [
                    {
                        heading: 'SLA',
                        measurementName: 'SLA',
                        aggregateFunction: 'SUM',
                        details: null,
                    },
                ];
            }

            if (
                request.additional.slaConfig.workingHours &&
                request.additional.slaConfig.applyWorkingHours
            ) {
                [
                    'MONDAY',
                    'TUESDAY',
                    'WEDNESDAY',
                    'THURSDAY',
                    'FRIDAY',
                    'SATURDAY',
                    'SUNDAY',
                ].forEach(day => {
                    if (
                        !request.additional.slaConfig.workingHours[day] ||
                        request.additional.slaConfig.workingHours[day].length < 2
                    ) {
                        request.additional.slaConfig.workingHours[day] = [0, 0];
                    }
                });
            }

            const holidayHours = [];

            if (request.additional.slaConfig.inactiveDays) {
                request.additional.slaConfig.inactiveDays
                    .filter(inactiveDay => {
                        return (
                            inactiveDay.inactiveHours[0] !== null &&
                            inactiveDay.inactiveHours[1] !== null
                        );
                    })
                    .forEach(inactiveDay => {
                        holidayHours.push(inactiveDay.inactiveHours[0]);
                        holidayHours.push(inactiveDay.inactiveHours[1]);
                    });
            }

            request.projections.forEach(projection => {
                projection.details = Object.assign(projection.details || {}, {
                    workingHours: request.additional.slaConfig.applyWorkingHours
                        ? request.additional.slaConfig.workingHours
                        : {},
                    holidayHours,
                    tzOffset: request.additional.slaConfig.tzOffset,
                    slaObjective: request.additional.slaConfig.slaObjective,
                    configTzLocation: request.additional.slaConfig.timezone,
                    ranges: request.additional.slaConfig.slaIntervals,
                });
            });

            request.groupBys.forEach(groupBy => {
                groupBy.details = Object.assign(groupBy.details || {}, {
                    workingHours: request.additional.slaConfig.applyWorkingHours
                        ? request.additional.slaConfig.workingHours
                        : {},
                    holidayHours,
                    tzOffset: request.additional.slaConfig.tzOffset,
                    slaObjective: request.additional.slaConfig.slaObjective,
                    configTzLocation: request.additional.slaConfig.timezone,
                    ranges: request.additional.slaConfig.slaIntervals,
                });
            });

            request.additional.slaConfig = JSON.stringify(request.additional.slaConfig); // Convert JSON to string of JSON.
        }
    }

    async getDataFromSpace(
        request: AnalyticsRequest,
        cancelSource?: CancelTokenSource,
        skipDimensionLookups?: boolean
        // @ts-ignore
    ): PromiseWithCancel<AnalyticsResult> {
        if (!cancelSource) {
            cancelSource = Axios.CancelToken.source();
        }
        if (request.reportingEngine === 'HISTORIC_TRENDS') {
            this.buildTrendsRequest(request);
        }

        request = await this.applyGroupLimits(request, cancelSource);

        AnalyticsService.applyProjectionFilters(request);

        // Account and client filter lookups and other ranked filter queries
        await this.applyFilters(request, cancelSource);

        const slaConfig = this.isJson(request?.additional?.slaConfig)
            ? JSON.parse(request?.additional?.slaConfig)
            : request?.additional?.slaConfig;
        if (slaConfig?.id) {
            const slaConfigToApply = await this.getSlaConfig(request, slaConfig.id);
            this.applySlaConfig(request, slaConfigToApply);
        }

        // Add originatingApp
        if (!request.additional?.originatingApp) {
            request.additional = {
                ...request.additional,
                originatingApp: this.applicationIdentifier,
            };
        }

        const batchRequest = toSpaceBatchRequest(request);
        const responses = await this.sprinklrAPIService.spaceBatchQuery(batchRequest, cancelSource);

        let dataSets = AnalyticsService.mergeDataSets(
            Object.keys(responses).reduce((sets, key) => {
                const spaceRequest = batchRequest.requests[key];
                sets[key] = AnalyticsService.processData(request, spaceRequest, responses[key]);
                return sets;
            }, {}),
            batchRequest,
            request
        );

        // if there is a batch request with more than one request, the dataSets may need to be sorted
        if (Object.keys(batchRequest.requests).length > 1 && request.sorts?.[0]) {
            dataSets = this.sortDataSets(dataSets, request);
        }

        if (request.viralTrends?.length > 0) {
            dataSets[0] = await this.applyViralTrendLookups(
                request,
                dataSets[0],
                request.viralTrends
            );
        }

        let promise: Promise<AnalyticsResult>;

        if (skipDimensionLookups) {
            promise = Promise.resolve({
                request,
                dataSets,
            });
        } else {
            const dimensionLookups: DimensionLookup[] = this.getDimensionLookups(dataSets, request);
            if (dimensionLookups.length === 0) {
                // no dimension lookups needed, resolve a promise with our result
                promise = Promise.resolve({
                    request,
                    dataSets,
                });
            } else {
                promise = this.applyDimensionLookups(
                    dataSets,
                    dimensionLookups,
                    request,
                    cancelSource
                );
            }
        }

        (promise as any).cancel = function(message?: string) {
            cancelSource.cancel(message);
        };

        return promise as PromiseWithCancel<AnalyticsResult>;
    }

    async applyViralTrendLookups(
        request: AnalyticsRequest,
        dataSet: DataSet,
        contibutors?: ViralContributor[]
    ): Promise<DataSet> {
        if (!dataSet.viralRows?.length) {
            return dataSet;
        }

        const supportedEngines: AnalyticsEngine[] = Object.keys(PostsService.engines).filter(
            engine => {
                return PostsService.engines[engine].postKey === 'ES_MESSAGE_ID';
            }
        ) as AnalyticsEngine[];

        const promises: Promise<ViralRowData>[] = dataSet.viralRows.map(async rowIndex => {
            const rowValues = dataSet.rows[rowIndex];
            const viralRequest = AnalyticsService.buildViralTrendsRequest(
                toReportingRequests(request)[0],
                rowValues,
                contibutors
            );
            let insights: ViralInsight[] = [];
            try {
                insights = await this.sprinklrAPIService.getViralTrends(viralRequest);
            } catch (e) {
                console.error('Error fetching viralTrends', e);
            }

            let posts = [];

            if (insights.length > 0 && supportedEngines.includes(request.reportingEngine)) {
                const universalMessageIds: string[] = [];
                insights.forEach(insight => {
                    if (insight.insightUMId) {
                        universalMessageIds.push(insight.insightUMId);
                    }
                });

                const postsProvider = new PostsProviderReporting(this.sprinklrAPIService);
                const imageService = new SocialNetworkImageService(
                    (this.sprinklrAPIService as any).axios
                );
                const resolver = new PostsResolve(this.sprinklrAPIService, {} as any, imageService);

                posts = PostsTransform.transform(
                    await postsProvider.getPostsById(
                        universalMessageIds,
                        {},
                        new WidgetDimensionKey(
                            PostsService.engines[request.reportingEngine].postKey
                        ),
                        request.reportingEngine,
                        request.report,
                        TimePeriodService.createFromAnalyticsTimePeriod(request.timePeriods[0])
                    )
                );

                posts = await resolver
                    .fixExpiredMedia(posts)
                    .then(PostsTransform.addThumbsToImages)
                    .then(resolvedPosts => {
                        return resolver.filterBrokenImages(resolvedPosts, false);
                    });
            }

            return {
                rowIndex,
                rowValues,
                insights,
                posts,
            };
        });

        try {
            (dataSet as any).viralTrends = await Promise.all(promises);
        } catch (e) {
            console.error('Error fetching viralTrends', e);
        }

        return dataSet;
    }

    async getData(
        request: AnalyticsRequest,
        cancelSource?: CancelTokenSource,
        dataSetOverride?: DataSet,
        skipDimensionLookups?: boolean
        // @ts-ignore
    ): PromiseWithCancel<AnalyticsResult> {
        if (!cancelSource) {
            cancelSource = Axios.CancelToken.source();
        }

        request = await this.applyGroupLimits(request, cancelSource);

        if (request.reportingEngine === 'HISTORIC_TRENDS') {
            this.buildTrendsRequest(request);
        }

        AnalyticsService.applyProjectionFilters(request);

        // Account and client filter lookups and other ranked filter queries
        await this.applyFilters(request, cancelSource);

        const additionalRequests = dataSetOverride ? null : AnalyticsService.splitRequests(request);
        const slaConfig = this.isJson(request?.additional?.slaConfig)
            ? JSON.parse(request?.additional?.slaConfig)
            : request?.additional?.slaConfig;
        if (slaConfig?.id) {
            const slaConfigToApply = await this.getSlaConfig(request, slaConfig.id);
            this.applySlaConfig(request, slaConfigToApply);
        }

        const requests: ReportingRequest[] = toReportingRequests(request);
        if (!requests || !requests.length) {
            return null;
        }

        let promises: Array<PromiseWithCancel<DataSet>>;

        if (!requests[0].groupBys || requests[0].groupBys.length === 0) {
            promises = requests.map(requestData => {
                return this.requestData(requestData, request, cancelSource);
            });
        } else {
            // If there are additional time periods, we should restrict the previous periods to the dimension values
            // returned by the initial time period request

            let firstRequest = requests[0];

            // dataSetOverride is set when processing 2nd, 3rd, etc. projections that use different report types.
            // In this case, set the filters for the first request as well 2nd.
            if (dataSetOverride) {
                firstRequest = JSON.parse(JSON.stringify(firstRequest));
                this.setFilters(firstRequest, request, dataSetOverride);
            }

            const originalPromise: PromiseWithCancel<DataSet> = this.requestData(
                firstRequest,
                request,
                cancelSource
            );
            promises = [originalPromise];

            let i,
                l = requests.length;
            for (i = 1; i < l; ++i) {
                ((requestData: ReportingRequest) => {
                    promises.push(
                        originalPromise.then((dataSet: DataSet):
                            | DataSet
                            | PromiseWithCancel<DataSet> => {
                            if (dataSet.rows.length === 0) {
                                return new DataSet(
                                    dataSet.dimensions,
                                    dataSet.metrics,
                                    [],
                                    dataSet.totals,
                                    dataSet.viralRows
                                );
                            }

                            requestData = JSON.parse(JSON.stringify(requestData));

                            this.setFilters(requestData, request, dataSetOverride || dataSet);

                            return this.requestData(requestData, request, cancelSource).then(
                                (dataSet2: DataSet) => {
                                    // console.log('previous dataSet', dataSet2);
                                    return dataSet2;
                                }
                            );
                        })
                    );
                })(requests[i]);
            }
        }

        const dataSets: DataSet[] = await Axios.all(promises);

        if (request.viralTrends?.length > 0) {
            dataSets[0] = await this.applyViralTrendLookups(
                request,
                dataSets[0],
                request.viralTrends
            );
        }

        const dimensionLookups: DimensionLookup[] = this.getDimensionLookups(dataSets, request);
        let promise: Promise<AnalyticsResult>;

        if (dimensionLookups.length === 0 || skipDimensionLookups === true) {
            // no dimension lookups needed, resolve a promise with our result
            promise = Promise.resolve({
                request,
                dataSets,
            });
        } else {
            promise = this.applyDimensionLookups(dataSets, dimensionLookups, request, cancelSource);
        }

        if (additionalRequests) {
            // Copy the DataSet before dimension lookups are applied
            const dataSetRaw = new DataSet(
                dataSets[0].dimensions,
                dataSets[0].metrics,
                dataSets[0].rows,
                dataSets[0].totals,
                dataSets[0].viralRows,
                [],
                false
            );

            promise = promise.then(
                (result: AnalyticsResult): AnalyticsResult => {
                    const promises: Array<PromiseWithCancel<
                        AnalyticsResult
                    >> = additionalRequests.map(request => {
                        return this.getData(request, cancelSource, dataSetRaw);
                    });

                    const promise: any = Axios.all(promises);

                    return promise.then(
                        (results2: AnalyticsResult[]): AnalyticsResult => {
                            // Need to integrate results2 values into result
                            result.dataSets.forEach((dataSet: DataSet, offset: number) => {
                                results2.forEach((result2: AnalyticsResult) => {
                                    const orderAdditional = result2.request.projections.map(
                                        (projection: AnalyticsProjection) => {
                                            return projection.details.origOrder;
                                        }
                                    );

                                    // Note: don't use dataSet as arg because we're overwriting it and with
                                    // multiple datasets, you'll get stale result
                                    result.dataSets[offset] = DataSet.merge(
                                        result.dataSets[offset],
                                        result2.dataSets[offset],
                                        orderAdditional
                                    );
                                });
                            });

                            return result;
                        }
                    );
                }
            );
        }

        if (request.includeTotal) {
            promise = promise.then(
                (result: AnalyticsResult): AnalyticsResult => {
                    const dataSetRaw = new DataSet(
                        dataSets[0].dimensions,
                        dataSets[0].metrics,
                        dataSets[0].rows,
                        dataSets[0].totals,
                        dataSets[0].viralRows,
                        [],
                        false
                    );
                    const totalRequests: AnalyticsRequest[] = [
                        {
                            ...request,
                            // was getting the groupBys in the filters for multiDimensional so we just use the first filter
                            filters: [request.filters[0]],
                            groupBys: [],
                            includeTotal: false,
                        },
                    ];
                    const totalPromises: Array<PromiseWithCancel<
                        AnalyticsResult
                    >> = totalRequests.map(totalRequest => {
                        return this.getData(totalRequest, cancelSource, dataSetRaw);
                    });
                    const totalPromise: any = Axios.all(totalPromises);
                    return totalPromise.then(
                        (totalResults: AnalyticsResult[]): AnalyticsResult => {
                            result.dataSets.forEach((dataSet: DataSet, offset) => {
                                totalResults.forEach((totalResult: AnalyticsResult) => {
                                    const newDataSet = new DataSet(
                                        result.dataSets[offset].dimensions,
                                        result.dataSets[offset].metrics,
                                        result.dataSets[offset].rows,
                                        totalResult.dataSets[0].rows[0],
                                        undefined,
                                        undefined,
                                        false
                                    );
                                    result.dataSets[offset] = newDataSet;
                                    result.request.includeTotal = false;
                                });
                            });
                            return result;
                        }
                    );
                }
            );
        }

        (promise as any).cancel = function(message?: string) {
            cancelSource.cancel(message);
        };

        return promise as PromiseWithCancel<AnalyticsResult>;
    }

    rankedFilter = (filter, request: AnalyticsRequest, cancelSource) => {
        // We want to copy the request, but grouped by the RANK dimension, and then find the corresponding value
        // for the dimension at the rank position, and use that as a filter on the primary request.
        // Main use-case is for correlating with another chart that's showing a grouping by the same dim for
        // similar data with different visualization - e.g. bar chart of mention count grouped by country,
        // then word clouds that correspond to each of the top three bars.
        const rank = parseInt(filter.values?.[0]); // gets turned into a string somewhere...
        if (rank < 1) {
            return Promise.reject(`Rank must be 1 or greater, given value ${rank} is invalid.`);
        }

        const requestCopy: AnalyticsRequest = JSON.parse(JSON.stringify(request));
        requestCopy.includeTotal = false;

        if (
            (!requestCopy.timePeriods || !requestCopy.timePeriods.length) &&
            ((request as any) as PostsRequest).timePeriod
        ) {
            requestCopy.timePeriods = [{ ...((request as any) as PostsRequest).timePeriod }];
        }
        if (requestCopy.timePeriods.length > 1) {
            requestCopy.timePeriods.length = 1;
        }

        requestCopy.groupBys.length = 0;

        let groupBy: AnalyticsGroupBy;

        if (filter.details?.srcType === 'CUSTOM' && filter.details?.fieldName) {
            groupBy = {
                heading: filter.details.fieldName,
                dimensionName: filter.dimensionName,
                groupType: 'FIELD',
                details: {
                    fieldName: filter.details.fieldName,
                    srcType: filter.details.srcType,
                },
            };
        } else {
            groupBy = {
                heading: 'dim_' + filter.dimensionName,
                dimensionName: filter.dimensionName,
                groupType: 'FIELD',
                details: null,
            };
        }

        requestCopy.groupBys.push(groupBy);

        const sorts = requestCopy.sorts.filter(s =>
            requestCopy.projections.some(p => p.heading === s.heading)
        );
        requestCopy.sorts.length = 0;
        requestCopy.sorts.push(...sorts);

        const filters = requestCopy.filters.filter(f => f.filterType !== 'RANK');
        requestCopy.filters.length = 0;
        requestCopy.filters.push(...filters);

        // If the limit is less than the rank, increase it to equal the rank so that the results include the desired ranked item to filter by
        if (requestCopy.limit < rank) {
            requestCopy.limit = rank;
        }
        if (filter.dimensionName === 'COUNTRY') {
            // Special implicit COUNTRY NIN "UN" filter for parity with other places this is implicitly applied.
            requestCopy.filters.push({
                dimensionName: 'COUNTRY',
                filterType: 'NIN',
                values: ['UN'], // think this is abbreviation of "unknown", not "United Nations"
                details: null,
            });
        }

        return this.getDataFromSpace(requestCopy, cancelSource, true).then(result => {
            // Must resolve the concrete values using a separate request - RANK requires a subquery.
            filter.values.length = 0;
            filter.filterType = 'IN';

            if (result?.dataSets?.length > 0) {
                const rankRow = result.dataSets[0].rows[rank - 1]; // rank is 1-based

                const dimIdx = result.dataSets[0].fields.findIndex(f => f.name === groupBy.heading);

                const dimVal = rankRow && rankRow[dimIdx];

                if (!rankRow) {
                    filter.details.noValuesForRankFilter = true;
                } else if (typeof dimVal === 'string' || typeof dimVal === 'number') {
                    filter.values.push(dimVal);
                } else if (dimVal?.id) {
                    filter.values.push(dimVal.id);
                } else {
                    throw Error(`Malformed rank dimension ${dimVal}`);
                }
            }
            return filter;
        });
    };

    getDatas(
        requests: AnalyticsRequest[],
        cancelSource?: CancelTokenSource
    ): PromiseWithCancel<AnalyticsResult[]> {
        if (!cancelSource) {
            cancelSource = Axios.CancelToken.source();
        }

        if (
            requests.some(
                request => 'RDB_FIREHOSE' === request.reportingEngine && !request.filters?.length
            )
        ) {
            // TODO: Add top-level query validation for other engines as well.
            return Promise.reject(
                i18n.t('Error: Missing query. Please configure a query in the data tab.')
            );
        }

        // if the request is for a Philippines province region map, set limit to 800 so that city data can be aggregated -AMR
        requests.forEach((request, i) => {
            if (request.groupBys?.[0]?.details?.regionType === 'province') {
                const copiedRequest = JSON.parse(JSON.stringify(request));
                copiedRequest.limit = 800;
                requests[i] = copiedRequest;
            }
            // Limit story message queries to a max of 100 rows.
            if (request.reportingEngine === 'STORY_MESSAGE') {
                const copiedRequest = JSON.parse(JSON.stringify(request));
                copiedRequest.limit = Math.min(copiedRequest.limit, 100);
                requests[i] = copiedRequest;
            }
        });

        if (
            ['US_STATE', 'COUNTRY', 'COUNTRY_SUBDIVISION'].includes(
                requests[0]?.groupBys?.[0]?.dimensionName
            )
        ) {
            // this flag is required by space api to return correct data for world heat map
            requests[0].additional.chartType = 'MAP';
        }
        // copying request for WorldHeatMap displaying both city and region data
        if (
            ['US_STATE', 'COUNTRY', 'COUNTRY_SUBDIVISION'].includes(
                requests[1]?.groupBys[0].heading
            )
        ) {
            const heading = requests[1]?.groupBys[0].heading;

            const copiedRequest = JSON.parse(JSON.stringify(requests[0]));

            copiedRequest.groupBys[0].heading = heading;
            copiedRequest.groupBys[0].dimensionName = heading;
            copiedRequest.groupBys[0].details.alternateHeading = heading;

            requests[1] = copiedRequest;
        }

        // special key that needs to be set for TOPIC_CLUSTER to return with data
        if (TOPIC_CLUSTER_HEADINGS.includes(requests[0]?.groupBys?.[0]?.dimensionName)) {
            _set(requests[0].additional, 'TOPIC_CLUSTER', true);
        }

        const promises: Array<PromiseWithCancel<AnalyticsResult>> = requests.map(request => {
            return this.getDataFromSpace(request, cancelSource);
        });

        const promise: any = Axios.all(promises);

        promise.cancel = function(message?: string) {
            cancelSource.cancel(message);
        };

        return promise as PromiseWithCancel<AnalyticsResult[]>;
    }

    getSlaConfig(request: AnalyticsRequest, slaConfigId: string) {
        this.metaInfoService.loadSLAPresets(request.reportingEngine);

        return MetaInfoService.loaderSLAPresets.then(() =>
            this.metaInfoService
                .getSLAPresets(request.reportingEngine)
                .find(preset => preset.id === slaConfigId)
        );
    }

    static mergeDataSets = (
        dataSets: { [key: string]: DataSet },
        batchRequest: SpaceBatchRequest,
        origWidgetRequest: AnalyticsRequest
    ): DataSet[] => {
        let primary: DataSet;
        let previousPeriod: DataSet;
        const multiNonPreviousTimePeriods: DataSet[] = [];

        Object.keys(dataSets)
            // we sort so the dataSet merge goes correctly
            // this helps with the projection decoration order
            .sort((keyA, keyB) => {
                const maxA = Math.max(
                    ...getProjectionOrigOrderFromRequest(
                        batchRequest.requests[keyA],
                        origWidgetRequest
                    )
                );
                const maxB = Math.max(
                    ...getProjectionOrigOrderFromRequest(
                        batchRequest.requests[keyB],
                        origWidgetRequest
                    )
                );
                return maxA - maxB;
            })
            .forEach(key => {
                const request = batchRequest.requests[key];
                if (!request) {
                    throw new Error(
                        `Request key ${key} not found in [${Object.keys(batchRequest).join(', ')}]`
                    );
                }

                const resolvedOrderAdditional = getProjectionOrigOrderFromRequest(
                    request,
                    origWidgetRequest
                );

                const dataSet = dataSets[key];
                if (key.endsWith('_PREV')) {
                    if (!previousPeriod) {
                        previousPeriod = dataSet;
                    } else {
                        previousPeriod = DataSet.merge(
                            previousPeriod,
                            dataSet,
                            resolvedOrderAdditional
                        );
                    }
                    return;
                }

                if (!primary) {
                    primary = dataSet;
                } else if (key.includes(MULTI_TIME_PERIOD_KEY)) {
                    multiNonPreviousTimePeriods.push(dataSet);
                } else {
                    primary = DataSet.merge(primary, dataSet, resolvedOrderAdditional);
                }
            });

        const additionalDataSets =
            multiNonPreviousTimePeriods.length > 0 ? multiNonPreviousTimePeriods : [previousPeriod];

        const merged = [primary, ...additionalDataSets].filter(dataSet => !!dataSet);
        //ensure order of projections matches the original request after merging
        const reordered = ensureProjectionOrder(merged, origWidgetRequest);

        return reordered;
    };

    /**
     * TODO: move to helpers when package and import flow direction is sorted out
     *
     * @param headings
     * @param groupBys
     * @param projections
     */
    static buildDataSetFields = (
        headings: string[],
        groupBys: AnalyticsGroupBy[] = [],
        projections: AnalyticsProjection[] = [],
        report: string
    ) => {
        groupBys = JSON.parse(JSON.stringify(groupBys));
        projections = JSON.parse(JSON.stringify(projections));

        const dimensions: Dimension[] = [];
        const metrics: Metric[] = [];

        const topicClusterHeading = getTopicClusterHeading(headings);
        if (topicClusterHeading) {
            dimensions.push(new Dimension(topicClusterHeading));
        }

        headings.forEach(heading => {
            const groupByIndex = groupBys.findIndex(
                g => g.heading === heading || g.dimensionName === heading
            );
            if (groupByIndex !== -1) {
                const groupBy = groupBys[groupByIndex];
                let resolvedHeading = groupByOrProjectionAlternateHeading(groupBy);

                // Don't use the alternate heading if the groupby is CITY - we need to check for the CITY dimension name in World Heat Map Widget.
                if (groupBy.heading === 'CITY') {
                    resolvedHeading = groupBy.heading;
                }

                const dimension = new Dimension(
                    resolvedHeading,
                    undefined,
                    groupBy.details?.dataType
                );

                if (groupBy.groupType === 'DATE_HISTOGRAM') {
                    dimension.type = 'TIMESTAMP';
                }

                if (groupBy.details?.dataType) {
                    dimension.dataType = groupBy.details?.dataType as FieldDataType;
                    if (groupBy.details.dataType === 'DATETIME') {
                        dimension.type = 'TIME_INTERVAL';
                    }
                }

                dimensions.push(dimension);
                groupBys.splice(groupByIndex, 1);
                return;
            }

            const projectionIndex = projections.findIndex(
                p =>
                    buildAnalyticsProjectionHeader(
                        p.measurementName,
                        p.aggregateFunction,
                        p.details?.origReport || report,
                        p.details
                    ) === heading || p.heading === heading
            );

            if (projectionIndex !== -1) {
                const projection = projections[projectionIndex];

                const type = getType(projection.details?.dataType);
                const dataType = getDataType(
                    projection.details?.dataType,
                    projection.aggregateFunction
                );

                // Use label to make sure the projection heading is readable
                const metricHeading = getHeading(projection, metrics);

                metrics.push(
                    new Metric(
                        metricHeading,
                        type,
                        dataType,
                        projection.aggregateFunction,
                        !!projection.details?.alternateHeading
                    )
                );
                projections.splice(projectionIndex, 1);
            }
        });

        if (headings.length !== dimensions.length + metrics.length) {
            console.warn(
                `Response has ${headings.length} headers but only ${dimensions.length} dimensions and ${metrics.length} metrics matched`
            );
        }

        return {
            dimensions,
            metrics,
        };
    };

    static processData = (
        request: AnalyticsRequest,
        apiRequest: SpaceRequest | ReportingRequest,
        data: ReportingResponse
    ): DataSet => {
        data.headings = data.headings || [];
        data.rows = data.rows || [];

        // If any of our metrics used aggregate functions
        // CHANGE, PERCENTAGE_CHANGE, or PERCENTAGE,
        // massage the headings and rows to remove the redundant data
        //
        // Note: moved up here, with redundant looping because the code
        // directly below breaks the decorator truncation.
        if (apiRequest.projectionDecorations && apiRequest.projectionDecorations.length) {
            data = removeUnusedDecorators(
                data,
                request.projections,
                request.groupBys?.length ?? 0,
                request.report
            );
        }

        const { dimensions, metrics } = AnalyticsService.buildDataSetFields(
            data.headings,
            request.groupBys,
            request.projections,
            request.report
        );

        data.rows.forEach((row, i) => {
            const originalText = data.additional?.[i]?.[0]?.ORIGINAL_TEXT;
            const translatedText = data.additional?.[i]?.[0]?.TRANSLATED_TEXT;

            if (translatedText && row[0] === originalText) {
                row.splice(0, 1, translatedText);
            }
        });

        // If there's a filter w/ details.noValuesForRankFilter, it means that the rank filter returned no values & the dataset should be empty
        const noValuesForRankFilter =
            request.filters?.length &&
            request.filters.some(filter => filter.details?.noValuesForRankFilter);

        if (noValuesForRankFilter) {
            data.rows = [];
        }

        return new DataSet(dimensions, metrics, data.rows, data.totals, data.viralRows);
    };

    requestData(
        requestData: ReportingRequest,
        request: AnalyticsRequest,
        cancelSource?: CancelTokenSource
    ): PromiseWithCancel<DataSet> {
        if (!cancelSource) {
            cancelSource = Axios.CancelToken.source();
        }

        let promise: PromiseWithCancel<DataSet> | PromiseWithCancel<ReportingResponse> = null;

        promise = this.sprinklrAPIService.query(requestData, cancelSource);

        promise = promise.then(
            (data: ReportingResponse): DataSet => {
                data.headings = data.headings || [];
                data.rows = data.rows || [];
                data.additional = data.additional || [];

                // If any of our metrics used aggregate functions
                // CHANGE, PERCENTAGE_CHANGE, or PERCENTAGE,
                // massage the headings and rows to remove the redundant data
                //
                // Note: moved up here, with redundant looping because the code
                // directly below breaks the decorator truncation.
                if (requestData.projectionDecorations && requestData.projectionDecorations.length) {
                    data = removeUnusedDecorators(
                        data,
                        request.projections,
                        request.groupBys?.length ?? 0,
                        request.report
                    );
                }

                data = pivotTopicCluster(data, request.projections[0], requestData.pageSize);

                const { dimensions, metrics } = AnalyticsService.buildDataSetFields(
                    data.headings,
                    request.groupBys,
                    request.projections,
                    request.report
                );

                data.rows.forEach((row, i) => {
                    const originalText = data.additional?.[i]?.[0]?.ORIGINAL_TEXT;
                    const translatedText = data.additional?.[i]?.[0]?.TRANSLATED_TEXT;

                    if (translatedText && row[0] === originalText) {
                        row.splice(0, 1, translatedText);
                    }
                });

                return new DataSet(dimensions, metrics, data.rows, [], data.viralRows);
            }
        ) as PromiseWithCancel<DataSet>;

        promise.cancel = (message?: string) => {
            cancelSource.cancel(message);
        };

        return promise as PromiseWithCancel<DataSet>;
    }

    private setFilters(
        requestData: ReportingRequest,
        request: AnalyticsRequest,
        dataSet: DataSet
    ): AnalyticsFilter[] {
        let dimension: Dimension;
        let dimValues: string[];
        request.groupBys.forEach((groupBy: AnalyticsGroupBy, index: number) => {
            // WORD_CLOUD_MESSAGE and TOPIC_IDS filters don't behave correctly right now
            if (!AnalyticsService.canFilter(request.reportingEngine, groupBy)) {
                requestData.pageSize = Math.max(requestData.pageSize, 100);
                return null;
            }

            dimension = dataSet.dimensions[index];
            dimValues = dataSet.categories(dimension);

            let found = false;
            requestData.filters = requestData.filters.map((filter: AnalyticsFilter) => {
                filter = {
                    dimensionName: filter.dimensionName,
                    filterType: filter.filterType,
                    values: filter.values,
                    details: filter.details,
                };

                if (
                    filter.dimensionName === groupBy.dimensionName &&
                    filter.filterType === 'IN' &&
                    filter.details &&
                    groupBy.details &&
                    filter.details.fieldName === groupBy.details.fieldName
                ) {
                    filter.values = dimValues;
                    found = true;
                }

                return filter;
            });

            if (!found) {
                requestData.filters.push({
                    dimensionName: groupBy.dimensionName as any,
                    details: groupBy.details,
                    filterType: 'IN',
                    values: dimValues,
                });
            }
        });

        return requestData.filters;
    }

    // Split off requests that have reports that are different than main report.
    // This is used to support automatically splitting a single request into
    // multiple requests, each with a different report type.
    static splitRequests(request: AnalyticsRequest): AnalyticsRequest[] {
        let additional: AnalyticsRequest[] = null;

        if (request.projections?.length) {
            let projection: AnalyticsProjection;
            if (request.sorts?.length) {
                request.projections.forEach(projection => {
                    if (
                        projection.heading === request.sorts[0].heading &&
                        projection.details?.origReport
                    ) {
                        request.report = projection.details?.origReport;
                    }
                });
            }
            const baseReport = request.report;
            let reportRequest: AnalyticsRequest;
            let report: string;
            const map: { [report: string]: AnalyticsRequest } = {};

            let x = request.projections.length;
            while (x--) {
                projection = request.projections[x];

                if (!projection.details) {
                    projection.details = {};
                }

                // If this metric has a different report, then clone this
                // request for that metric and add to our requests array
                if (projection.details.origReport && projection.details.origReport !== baseReport) {
                    // Remove this from the main request's projection list
                    request.projections.splice(x, 1);

                    // Find the request for this different report
                    report = projection.details.origReport;

                    reportRequest = map[report];
                    if (!reportRequest) {
                        // Not found?  Duplicate the first request
                        reportRequest = map[report] = JSON.parse(JSON.stringify(request));
                        reportRequest.report = report;
                        reportRequest.projections.length = 0;

                        if (!additional) {
                            additional = [];
                        }

                        additional.unshift(reportRequest);
                    }

                    // Append the projection to report's request
                    reportRequest.projections.unshift(projection);
                }

                // Need to store the order with the projection so the DataSet results
                // can be merged correctly after requests are fetched.
                projection.details.origOrder = x;
            }
        }

        return additional;
    }

    isJson(json) {
        try {
            JSON.parse(json);
            return true;
        } catch (e) {
            return false;
        }
    }

    sortDataSets(dataSets: DataSet[], request: AnalyticsRequest): DataSet[] {
        let sortLabel;
        request.projections?.length &&
            request.projections.forEach(projection => {
                if (
                    projection.heading === request.sorts[0].heading ||
                    projection.measurementName === request.sorts[0].heading
                ) {
                    sortLabel = projection.details?.alternateHeading || projection.details?.label;
                }
            });
        const sortField =
            sortLabel &&
            dataSets?.[0]?.fields?.find(field => {
                if (field.name === sortLabel) {
                    return field;
                }
            });
        return sortField
            ? dataSets.map(dataSet => {
                  const direction: Direction =
                      request.sorts[0].order === 'ASC' ? Direction.ASC : Direction.DESC;
                  return dataSet.sortByMetricDirection(sortField as Metric, direction);
              })
            : dataSets;
    }

    buildTrendsRequest(request: AnalyticsRequest) {
        if (!request.filters) {
            request.filters = [];
        }

        request.filters.push({
            filterType: 'IN',
            values: ['TWITTER'],
            dimensionName: 'HISTORIC_TREND_SOURCE',
        });

        request.groupBys = [
            {
                dimensionName: 'HISTORIC_TREND_TOPIC',
                heading: 'HISTORIC_TREND_TOPIC',
                groupType: 'FIELD',
            },
        ];

        request.projections = [
            {
                heading: 'HISTORIC_TREND_MENTIONS',
                measurementName: 'HISTORIC_TREND_MENTIONS',
                aggregateFunction: 'SUM',
                details: {
                    alternateHeading: 'MENTIONS',
                },
            },
        ];

        request.sorts = [
            {
                heading: 'HISTORIC_TREND_MENTIONS',
                order: 'DESC',
            },
        ];

        request.timePeriods = [];
        request.timePeriods[0] = toAnalyticsTimePeriod({ timeRange: 'TODAY' });
    }
}
